import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Search, MessageCircle, Edit, Trash2, Eye, Printer, Users, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, loadAuthToken } from "../lib/api";
import { isAdmin, isBranch, photoUrl, waLink, fmtDate } from "../lib/customers";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Avatar } from "../components/CustomerForm";
import { CustomerEditor } from "../components/customer/CustomerEditor";
import { TransferPanel } from "../components/customer/TransferPanel";

const STAT_CARDS = [
  ["total_customers", "Total Customers"], ["new_today", "New Today"], ["this_month", "This Month"], ["total_works", "Total Services"],
  ["pending_works", "Pending Services"], ["completed_works", "Completed"], ["followup_due", "Follow-up Due"],
];
const FILTERS = [["", "All Customers"], ["new", "New Customers (7 din)"], ["recent", "Recently Updated"], ["pending", "Pending Works"], ["completed", "Completed Works"], ["followup", "Follow-up Pending"]];

const Stats = ({ s }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mt-6">
    {STAT_CARDS.map(([k, l]) => (
      <div key={k} className={`rounded-xl border p-3 ${k === "followup_due" && s[k] ? "border-red-300 bg-red-50" : "border-slate-200 bg-white"}`} data-testid={`cust-stat-${k}`}>
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{l}</div>
        <div className="text-2xl font-semibold text-slate-900 mt-1">{s[k] ?? "—"}</div>
      </div>
    ))}
  </div>
);

const Row = ({ c, onView, onEdit, onPrint, onDelete }) => (
  <tr className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`cust-row-${c.id}`}>
    <td className="px-3 py-3">
      <button onClick={onView} className="flex items-center gap-3 text-left" data-testid={`cust-open-${c.id}`}>
        <Avatar src={photoUrl(c)} name={c.name} size="w-10 h-10" testid={`cust-avatar-${c.id}`} />
        <div><div className="font-semibold text-slate-900 hover:text-teal-700">{c.name}</div><div className="text-xs text-slate-500">{c.customer_code}</div></div>
      </button>
    </td>
    <td className="px-3 py-3 text-slate-700">{c.father_name || "—"}</td>
    <td className="px-3 py-3 whitespace-nowrap">{c.mobile}</td>
    <td className="px-3 py-3 text-slate-600">{c.village || "—"}</td>
    <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{c.branch_name || "Main (Head Office)"}</td>
    <td className="px-3 py-3 text-center">{c.total_works}</td>
    <td className="px-3 py-3 text-center">{c.pending_works ? <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs">{c.pending_works}</span> : 0}</td>
    <td className="px-3 py-3 whitespace-nowrap text-slate-600">{fmtDate(c.last_service_at)}</td>
    <td className="px-3 py-3">
      <div className="flex gap-1 justify-end">
        <Button size="icon" variant="ghost" onClick={onView} title="View" data-testid={`cust-view-${c.id}`}><Eye className="w-4 h-4" /></Button>
        <Button size="icon" variant="ghost" onClick={onEdit} title="Edit" data-testid={`cust-edit-${c.id}`}><Edit className="w-4 h-4" /></Button>
        <Button size="icon" variant="ghost" onClick={onPrint} title="Print" data-testid={`cust-print-${c.id}`}><Printer className="w-4 h-4" /></Button>
        {waLink(c) && <a href={waLink(c)} target="_blank" rel="noopener noreferrer" title="WhatsApp" className="inline-flex items-center justify-center w-9 h-9 rounded-md text-green-600 hover:bg-green-50" data-testid={`cust-wa-${c.id}`}><MessageCircle className="w-4 h-4" /></a>}
        {isAdmin() && <Button size="icon" variant="ghost" className="text-red-600" onClick={onDelete} title="Delete" data-testid={`cust-delete-${c.id}`}><Trash2 className="w-4 h-4" /></Button>}
      </div>
    </td>
  </tr>
);

export default function AdminCustomersPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0 });
  const [stats, setStats] = useState({});
  const [branches, setBranches] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [branch, setBranch] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const params = { page, page_size: 15, ...(q.trim() && { q: q.trim() }), ...(filter && { filter }), ...(branch && { branch }) };
      const [r, s] = await Promise.all([api.get("/customers", { params }), api.get("/customers/stats")]);
      setData(r.data); setStats(s.data);
    } catch (e) { if ([401, 403].includes(e?.response?.status)) navigate("/admin/login"); }
  };
  useEffect(() => {
    if (!loadAuthToken()) { navigate("/admin/login"); return; }
    if (!isBranch()) api.get("/admin/branches").then((r) => setBranches(r.data || [])).catch(() => {});
    // eslint-disable-next-line
  }, []);
  useEffect(() => { const id = setTimeout(load, 300); return () => clearTimeout(id); /* eslint-disable-next-line */ }, [q, filter, branch, page]);
  useEffect(() => { setPage(1); }, [q, filter, branch]);

  const del = async (c) => {
    if (!window.confirm(`${c.name} (${c.customer_code}) aur uske saare documents/works/notes delete karein?`)) return;
    try { await api.delete(`/customers/${c.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const pages = Math.max(1, Math.ceil(data.total / 15));

  return (
    <div data-testid="customers-page" className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4" data-testid="customers-back"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-slate-900 flex items-center gap-2"><Users className="w-6 h-6 text-teal-700" /> Customer Details</h1>
          <p className="text-sm text-slate-500 mt-1">Ek baar register karein — saare kaam, documents, receipts aur notes ek hi profile me.</p>
        </div>
        <Button onClick={() => setEditing({})} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="add-customer-btn"><Plus className="w-4 h-4 mr-1" /> Add New Customer</Button>
      </div>
      <Stats s={stats} />
      {!isBranch() && <TransferPanel onDone={load} />}
      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Naam, mobile, Customer ID, gaon, Application / Registration No…" className="pl-9" data-testid="customers-search" />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-9 rounded-md border border-input px-3 text-sm bg-white" data-testid="customers-filter">
          {FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {!isBranch() && (
          <select value={branch} onChange={(e) => setBranch(e.target.value)} className="h-9 rounded-md border border-input px-3 text-sm bg-white" data-testid="customers-branch-filter">
            <option value="">All Branches</option>
            <option value="main">Main (Head Office)</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name_en}</option>)}
          </select>
        )}
      </div>
      <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>{["Customer", "Father's Name", "Mobile", "Village", "Branch", "Works", "Pending", "Last Service", ""].map((h) => <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {data.items.length === 0 && <tr><td colSpan={9} className="text-center text-slate-500 py-10">Koi customer nahi mila.</td></tr>}
            {data.items.map((c) => (
              <Row key={c.id} c={c} onView={() => navigate(`/admin/customers/${c.id}`)} onEdit={() => setEditing(c)}
                onPrint={() => navigate(`/admin/customers/${c.id}?print=1`)} onDelete={() => del(c)} />
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-3 text-sm text-slate-600">
        <span data-testid="customers-total">Total: {data.total}</span>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="customers-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <span>{page} / {pages}</span>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="customers-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>
      {editing && (
        <CustomerEditor customer={editing.id ? editing : null} onClose={() => setEditing(null)}
          onSaved={(c) => { setEditing(null); editing.id ? load() : navigate(`/admin/customers/${c.id}`); }}
          onOpenExisting={(id) => navigate(`/admin/customers/${id}`)} />
      )}
    </div>
  );
}
